import { cloneDeep, get } from "lodash"
// @ts-ignore
import Papa from "papaparse"

import { CalculationDataTypeValue, CaseDetails, Team } from "../types"

import { calculatedValueDisplay } from "../mixins/calculatedValueDisplay"
import { formatDateMixin } from "../mixins/formatDateMixin"
import { CaseColumn } from "@/types/caseColumns"

import dataTypeFunctions from "./dataTypeFunctions"
import { TenantFeatures } from "@/types/tenantFeatures"

export const csvExport = {
	// If a value of a field is an object type, return a string value of that object
	objectToString (object: any) {
		if (object == null || object === undefined) return ""

		try {
			if (typeof (object) === "object") {
				return JSON.stringify(object)
			} else {
				return object
			}
		} catch (err) {
			return object
		}
	},

	fieldLabel (column: CaseColumn, value: any, label: string | null, record: CaseDetails) {
		if (column.dataType === "user" || column.dataType === "user-picture") {
			const usersName = record.users?.find((u: { id: string }) => u.id === value)
			return usersName ? usersName.name : this.objectToString(value)
		}

		if (column.dataType === "calculated") {
			return calculatedValueDisplay.methods.displayCalculatedField(
				record as CaseDetails,
				column.fieldExtensions,
				value,
				true)
		}

		if (column.fieldDataType && dataTypeFunctions[column.fieldDataType] && dataTypeFunctions[column.fieldDataType]!.toCsvString) {
			return dataTypeFunctions[column.fieldDataType]!.toCsvString!(column, value, label, record)
		}

		return label || this.objectToString(value)
	},

	// Converts the results into a downloaded-able CSV
	resultsToCsv (columns: CaseColumn[], resultGroups: readonly any[]) {
		let filteredArray: any[] = []
		const isGrouped = resultGroups.length > 1

		const csvHeader: any = columns.reduce((obj: { [key: string]: string }, column) => {
			obj[column.key] = column.label
			return obj
		}, {})

		if (isGrouped) {
			csvHeader["GroupBy"] = "Group By"
		}

		if (resultGroups) {
			resultGroups.forEach(resultGroup => {
				if (!resultGroup.results) { return }
				let mappedResult: any[] = resultGroup.results.map(
					(record: any) => {
						let mappedRecord = columns
							.reduce((obj: any, column) => {
								var value
								if (column.key.startsWith("calculation.")) {
									// LoDash interperates "." (for calculation.<id>) as a new object
									value = get(record, ["fields", column.key])
								} else {
									value = get(record, column.key)
								}
								if (value !== undefined) {
									let label: string | null = null
									if (column.dynamicField) {
										label = value.label
										value = value.value
									} else if (column.displayKey) {
										label = get(record, column.displayKey)
									}

									obj[column.key] = this.fieldLabel(
										column,
										value,
										label,
										record)
								}

								return obj
							}, {})

						if (isGrouped) {
							mappedRecord["GroupBy"] = resultGroup.label ? resultGroup.label : "None"
						}
						return mappedRecord
					})

				if (!filteredArray) {
					filteredArray = mappedResult
				} else {
					filteredArray = filteredArray.concat(mappedResult)
				}
			})
		}
		filteredArray.unshift(csvHeader)

		return "\ufeff" + Papa.unparse(filteredArray, { delimiter: ",", header: false, escapeFormulae: true })
	},

	addAdditionalColumnsForDownload (columns: any[], resultGroups: readonly any[], isFeatureEnabled: (feature: TenantFeatures) => boolean, caseName: string) {
		// add link to case beside case number if it is present and the feature is enabled
		if (isFeatureEnabled(TenantFeatures.ExportLinkToService) && columns.some(c => c.key === "caseNumber")) {
			const caseNoCol = columns.find(c => c.key === "caseNumber")
			const caseNoColIndex = columns.indexOf(caseNoCol)
			columns.splice(caseNoColIndex + 1, 0, {
				dataType: "text",
				fieldDataType: 0,
				key: "caseNumberAsUrl",
				label: caseName + " URL",
				fieldExtensions: {}
			})
			resultGroups.forEach(resultGroup => {
				resultGroup.results.forEach((result: any) => {
					result.caseNumberAsUrl =
						`${window.location.origin}${window.location.pathname}#/v2/cases/${result.caseNumber}`.replace(/ /g, "%20")
				})
			})
		}

		// add formatted text field values beside their normal columns if the feature is enabled
		if (isFeatureEnabled(TenantFeatures.TextFieldsFormattedExportColumn)) {
			const fieldTemplates: { [key: string]: string } = {}
			for (const col of columns) {
				if (col.dataType === "text" && col.fieldExtensions?.exportWithFormattedColumn) {
					const template = col.fieldExtensions?.exportColumnFormat
					const colHeading = col.fieldExtensions?.exportColumnHeading ?? ((col.label || "") + " (Formatted)").trim()
					if (!template) {
						continue
					}
					fieldTemplates[col.fieldId] = template
					const colIndex = columns.indexOf(col)
					columns.splice(colIndex + 1, 0, {
						dataType: "text",
						fieldDataType: 0,
						key: col.key + "-formatted",
						label: colHeading
					})
				}
			}

			for (const resultGroup of resultGroups) {
				for (const result of resultGroup.results) {
					if (!result.fields) continue
					Object.keys(result.fields).forEach((key: string) => {
						const val = result.fields[key].value
						if (key in fieldTemplates) {
							const valCopy = cloneDeep(val)
							const template = fieldTemplates[key]
							let formattedStr: string | null = null
							if (!!valCopy && valCopy.length > 0) {
								formattedStr = template.replace(/{value}/g, valCopy)
							}

							if (!!formattedStr && formattedStr.length > 0) {
								result.fields[key + "-formatted"] = formattedStr
							}
						}
					})
				}
			}
		}
	}
}
